import { Button, Divider, IconButton, Link, Menu, MenuItem, SvgIcon, SxProps, Theme, ThemeProvider, Tooltip } from '@mui/material';
import { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { PopupState, usePopupState } from 'material-ui-popup-state/hooks';
import { useCallback } from 'react';

export interface Action {
  label: string;
  divider?: boolean;
  link?: { href: string; target?: string };
  onClick?: () => void;
  isSelected?: boolean;
  'data-role'?: string;
}

export interface ActionMenuProps {
  actions: Action[];
  label?: string;
  Icon?: typeof SvgIcon;
  buttonStyles?: SxProps;
  AnchorComponent?: (props: ReturnType<typeof bindTrigger>) => JSX.Element;
  isSelected?: (item: Action) => boolean;
  uppercase?: boolean;
  menuItemStyles?: SxProps;
  theme: Theme;
}

const ActionMenuItem = (props: { action: Action; selected: boolean; itemStyle: SxProps; popupState: PopupState; 'data-role'?: string }) => {
  const { action, selected, itemStyle, popupState, 'data-role': dataRole } = props;

  const closePopup = useCallback(
    (action?: () => void) => {
      action?.();
      popupState.close();
    },
    [popupState]
  );

  if (action.onClick) {
    // Item is a click handler for some event: return a MenuItem that handles the callback
    // and closes the menu

    return (
      <MenuItem sx={itemStyle} selected={selected} onClick={() => closePopup(action.onClick)} tabIndex={0} data-role={dataRole}>
        {action.label}
      </MenuItem>
    );
  }
  if (action.link) {
    // Item is a link to a page: return a MenuItem with a embedded Link
    return (
      <MenuItem
        sx={itemStyle}
        component={Link}
        href={action.link.href}
        onClick={() => closePopup()}
        target={action.link.target}
        autoFocus={selected}
        className={selected ? 'Mui-selected' : ''}
        tabIndex={0}
        data-role={dataRole}
      >
        {action.label}
      </MenuItem>
    );
  }
  // Item is not interactive, disable pointer events
  return (
    <MenuItem sx={{ ...itemStyle, pointerEvents: 'none' }} selected={selected} data-role={dataRole}>
      {action.label}
    </MenuItem>
  );
};

export const ActionMenu = (props: ActionMenuProps) => {
  const { Icon, label, actions, buttonStyles, AnchorComponent: Component, isSelected, uppercase, menuItemStyles, theme } = props;
  const popupState = usePopupState({ variant: 'popover', popupId: `action-menu-${label}` });

  const getAnchorComponent = (popupState: PopupState) => {
    if (Component) {
      return <Component {...bindTrigger(popupState)}></Component>;
    }
    if (Icon) {
      return (
        <Tooltip title={label} placement="bottom">
          <IconButton sx={buttonStyles} {...bindTrigger(popupState)}>
            <Icon />
          </IconButton>
        </Tooltip>
      );
    }
    return (
      <Button sx={buttonStyles} {...bindTrigger(popupState)}>
        {label}
      </Button>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      {getAnchorComponent(popupState)}
      <Menu {...bindMenu(popupState)}>
        {actions.map((action) => {
          const itemStyle = { textTransform: uppercase ? 'uppercase' : undefined, ...menuItemStyles } as const;
          const selected = !!(action.isSelected || isSelected?.(action));
          const item = (
            <ActionMenuItem
              key={`item-${action.label}`}
              action={action}
              selected={selected}
              itemStyle={itemStyle}
              popupState={popupState}
              data-role={action['data-role']}
            />
          );
          if (action.divider) {
            return [item, <Divider key={`divider-${action.label}`} />];
          }
          return item;
        })}
      </Menu>
    </ThemeProvider>
  );
};
